import moment from 'moment';
import _ from 'lodash';
import { change, destroy } from 'redux-form';
import { push, replace } from 'react-router-redux';

import getDatabase from 'Common/DB';
import {
  DATE_FORMAT_FOR_SERVER,
  NOTIFICATION_DIALOG_MESSAGES,
} from 'Common/Constants';
import * as CommonActions from 'State/actions';
import * as CommonSelectors from 'State/selectors';
import * as POSSelectors from 'PointOfSale/selectors';
import { StatusEnum } from 'Common/Constants/Enumeration/StatusEnum';
import { MealTypesEnum } from 'Common/Constants/Enumeration/MealTypesEnum';
import { TransactionTypeEnum } from 'Common/Constants/Enumeration/TransactionTypeEnum';
import {
  addAllergensProhibitedItems,
  sortByLastNameFirstName,
  getItemWithData,
} from 'PointOfSale/modules/common/helpers';
import { TransactionEnum } from 'Common/FieldEnum';
import { TRANSACTION_FORM_NAME } from 'Common/FormEnum';
import * as IndividualServingActions from 'PointOfSale/modules/individualServing/state/actions';
import * as DialogActions from 'Components/dialogs/state/actions';
import { POS_ROUTES } from 'PointOfSale/modules/common/constants';
import {
  getProhibitedMessage,
  getAllergensMessageDialog,
  getSpecialMessageDialog,
} from 'PointOfSale/modules/common/helpers';
import * as POSActions from 'PointOfSale/state/actions';

import * as types from './types';

const SearchStatuses = [
  StatusEnum.Free,
  StatusEnum.Reduced,
  StatusEnum.Paid,
  StatusEnum.Teacher,
  StatusEnum.Adult,
  StatusEnum.SchoolFoodService,
  StatusEnum.Catering,
];

export const setTeacher = teacher => ({
  type: types.SET_TEACHER,
  payload: teacher,
});

export const setStudents = students => ({
  type: types.SET_STUDENTS,
  payload: students,
});

/**
 * Selects the teacher and retrieves students
 * @param {String} teacher
 */
export const selectTeacher = teacher => async (
  dispatch,
  getState,
  MPWWebAPI
) => {
  const state = getState();
  const isOnline = CommonSelectors.getOnline(state);
  const selectedSite = POSSelectors.getSelectedSiteDetails(state);
  const selectedDate = POSSelectors.getSelectedDate(state);
  const currentSelectedMenu = POSSelectors.getCurrentSelectedMenu(state);
  const PrimarySiteId = selectedSite.Id;
  const MealType = MealTypesEnum[currentSelectedMenu];
  const WorkingDate = moment(selectedDate).format(DATE_FORMAT_FOR_SERVER);
  const ReturnPicture = true;
  dispatch(CommonActions.loading(types.SET_LOADING, true));
  dispatch(setTeacher(teacher));
  dispatch(setStudents([]));
  let students = [];
  let siteIds = [PrimarySiteId];
  try {
    const databaseName = CommonSelectors.getDatabaseName(state);
    const DB = await getDatabase(databaseName);
    const site = await DB.Sites.where({
      Id: PrimarySiteId,
      PrimarySiteId,
    }).first();

    if (site) {
      const secondarySiteIds = site.SecondarySites.map(ss => ss.Id);
      siteIds.push(secondarySiteIds);
    }

    siteIds = _.uniq(_.flatten(siteIds));
    students = await DB.Students.where('Teacher')
      .startsWith(teacher)
      .and(student => siteIds.includes(student.SiteId))
      .and(student => SearchStatuses.includes(student.StatusCurrent))
      .toArray(sortByLastNameFirstName);

    if (isOnline) {
      try {
        students = await Promise.all(
          students.map(s =>
            MPWWebAPI.postGenericInsertUpdate('/Serving/GetStudentDetail', {
              IdSeed: s.IdSeed,
              PrimarySiteId,
              ReturnPicture,
              WorkingDate,
              MealType,
            }).then(r => ({
              ...s,
              ...r.data.Data,
            }))
          )
        );
      } catch (err) {
        dispatch(CommonActions.error(types.SET_ERROR, err));
      }
    }
  } catch (err) {
    dispatch(CommonActions.error(types.SET_ERROR, err));
  }

  students = students.map(s => ({
    ...s,
    selected: false,
    error: null,
    symbolIndicator: [],
    BoughtItems: [],
  }));

  students.forEach(s => {
    addAllergensProhibitedItems(s, s.symbolIndicator);
  });

  dispatch(setStudents(students));
  dispatch(CommonActions.loading(types.SET_LOADING, false));
};

export const addItem = (idSeed, item) => ({
  type: types.ADD_ITEM,
  payload: {
    idSeed,
    item,
  },
});

export const toggleAllForCheckout = shouldSelectAll => (dispatch, getState) => {
  dispatch(resetAllStudentsSelected());
  dispatch(resetAllStudentsError());
  dispatch(resetAllStudentsBoughtItems());

  const state = getState();

  const students = POSSelectors.getStudentsForTeacher(state);
  const qsi = POSSelectors.getQuickServeItem(state);

  if (!_.isEmpty(qsi) && shouldSelectAll) {
    const isOnline = CommonSelectors.getOnline(state);

    const canBuyIdSeeds = [];
    const cannotBuyIdSeeds = [];
    const dialogs = [];

    for (const student of students) {
      // const prohibitedItems = POSSelectors.getProhibitedItems(state);
      const currentSelectedMenu = POSSelectors.getCurrentSelectedMenu(state);
      const taxRate = POSSelectors.getCurrentTaxRate(state);
      const pendingItems = [getItemWithData(qsi, student)];

      const prohibitedMessage = getProhibitedMessage({
        profile: student,
        item: qsi,
        currentSelectedMenu,
        // prohibitedItems,
        boughtItems: [],
        pendingItems,
        taxRate,
        isOnline,
      });

      //student has item that is prohibited, show dialog and add error border
      if (prohibitedMessage) {
        dispatch(setStudentError(student.IdSeed, [prohibitedMessage]));
        cannotBuyIdSeeds.push({
          IdSeed: student.IdSeed,
        });
      } else {
        canBuyIdSeeds.push(student.IdSeed);

        // Compact student allergen and special message dialogs as this will remove them if they are null or undefined.
        const studentDialogs = _.compact([
          getAllergensMessageDialog(student),
          getSpecialMessageDialog(student),
        ]);

        dialogs.push(...studentDialogs);
      }
    }

    /** Not all students were checked */
    if (cannotBuyIdSeeds.length > 0) {
      dialogs.push(NOTIFICATION_DIALOG_MESSAGES.TEACHER_STUDENTS_NOT_ALLOWED);
    }

    /** If dialogs, add notification dialogs */
    if (dialogs.length > 0) {
      dispatch(DialogActions.addNotificationDialogs(dialogs));
    }

    if (canBuyIdSeeds.length > 0) {
      dispatch({
        type: types.TOGGLE_FOR_CHECKOUT,
        payload: {
          students: canBuyIdSeeds,
          selected: shouldSelectAll,
        },
      });

      dispatch(addItemToStudents(canBuyIdSeeds, qsi));
    } else {
      dispatch(setSelectAllStudents(false));
    }
  } else {
    dispatch({
      type: types.TOGGLE_FOR_CHECKOUT,
      payload: {
        students: students.map(s => s.IdSeed),
        selected: shouldSelectAll,
      },
    });
  }
};

export const toggleForCheckout = idSeed => (dispatch, getState) => {
  const prevState = getState();
  const prevStudentState = POSSelectors.getStudentForTeacher(prevState, idSeed);
  dispatch(resetStudentSelected(idSeed));
  dispatch(resetStudentError(idSeed));
  dispatch(resetStudentBoughtItems(idSeed));

  const state = getState();

  const student = POSSelectors.getStudentForTeacher(state, idSeed);
  const qsi = POSSelectors.getQuickServeItem(state);

  // const showAllergenDialog = POSSelectors.getShowAllergenDialog(state);
  // const showSpecialMessageDialog = POSSelectors.getShowSpecialMessageDialog(
  //   state
  // );

  let selected = !prevStudentState.selected;

  if (!_.isEmpty(qsi) && selected) {
    //make sure the item can be added
    // const prohibitedItems = POSSelectors.getProhibitedItems(state);
    const currentSelectedMenu = POSSelectors.getCurrentSelectedMenu(state);
    const taxRate = POSSelectors.getCurrentTaxRate(state);
    const pendingItems = [getItemWithData(qsi, student)];
    const isOnline = CommonSelectors.getOnline(state);

    const prohibitedMessage = getProhibitedMessage({
      profile: student,
      item: qsi,
      currentSelectedMenu,
      // prohibitedItems,
      boughtItems: [],
      pendingItems,
      taxRate,
      isOnline,
    });

    /** Student has item that is prohibited, show dialog and add error border */
    if (prohibitedMessage) {
      selected = false;

      dispatch(
        DialogActions.setNotificationDialog(
          NOTIFICATION_DIALOG_MESSAGES.TEACHER_STUDENT_NOT_ALLOWED
        )
      );

      dispatch(setStudentError(student.IdSeed, [prohibitedMessage]));
    } else {
      dispatch(addItem(student.IdSeed, qsi));

      const allergenMessage = getAllergensMessageDialog(student);
      const specialMessage = getSpecialMessageDialog(student);
      const dialogs = _.compact([allergenMessage, specialMessage]);

      /** Student has an allergen or special message, add notification to dialogs */
      if (dialogs.length > 0) {
        dispatch(DialogActions.addNotificationDialogs(dialogs));
      }
    }
  }

  dispatch({
    type: types.TOGGLE_FOR_CHECKOUT,
    payload: {
      students: [student.IdSeed],
      selected,
    },
  });
};

/**
 * @returns {Promise<Number>}
 */
export const checkout = () => async (dispatch, getState, MPWWebAPI) => {
  let numProcessed = 0;
  try {
    const state = getState();
    const databaseName = CommonSelectors.getDatabaseName(state);
    const DB = await getDatabase(databaseName);

    const {
      PrimarySiteId,
      MealType,
      WorkingDT,
      ServerId,
    } = POSSelectors.getServingDetails(state);

    const students = POSSelectors.getStudentsForTeacher(state);

    dispatch(CommonActions.loading(types.SET_WRITING, true));

    dispatch(setNumProcessed(0));
    const numSelectedStudents = students.filter(s => s.selected).length;
    dispatch(setNumProcessing(numSelectedStudents));

    for (const student of students) {
      /** Student is not selected, remove from list and move onto next */
      if (!student.selected) {
        dispatch(removeStudent(student.IdSeed));
        continue;
      }

      const StudentIdSeed = student.IdSeed;
      const BoughtItems = student.BoughtItems || [];
      const BoughtItemsWithData = [];

      for (const { ItemId, Qty = 1 } of BoughtItems) {
        const item = await DB.ServedItems.where({
          ItemId,
          SiteId: PrimarySiteId,
        }).first();

        const itemWithData = getItemWithData(item, student, Qty, MealType);
        BoughtItemsWithData.push(itemWithData);
      }

      const transaction = {
        TransactionType: TransactionTypeEnum.Charge,
        AmountPaid: student.AmountPaid || 0,
        PrimarySiteId,
        ServerId,
        MealType,
        StudentIdSeed,
        BoughtItems: BoughtItemsWithData,
        WorkingDT,
      };

      const [success, errors] = await dispatch(
        POSActions.processTransaction(
          transaction,
          { profile: student },
          { displayErrorMessages: false }
        )
      );

      if (!success && errors.length) {
        dispatch(setStudentError(StudentIdSeed, errors));
        continue;
      }

      if (success) {
        dispatch(removeStudent(StudentIdSeed));
        dispatch(replace(POS_ROUTES.TEACHER));
        numProcessed += 1;
        dispatch(setNumProcessed(numProcessed));

        dispatch(POSActions.fetchStudent(transaction.StudentIdSeed));
      }
    }

    if (numProcessed === numSelectedStudents) {
      dispatch(reset());
    }
  } catch (e) {
    dispatch(CommonActions.error(types.SET_ERROR, e));
  }
  dispatch(CommonActions.loading(types.SET_WRITING, false));
  return numProcessed;
};

export const removeStudent = idSeed => ({
  type: types.REMOVE_STUDENT,
  payload: idSeed,
});

export const reset = () => ({
  type: types.RESET,
});

export const setStudentError = (idSeed, error) => ({
  type: types.SET_STUDENT_ERROR,
  payload: {
    idSeed,
    error,
  },
});

export const resetStudentError = idSeed => ({
  type: types.RESET_STUDENT_ERROR,
  payload: {
    idSeed,
  },
});

export const onProfileSelect = profile => (dispatch, getState) => {
  dispatch(IndividualServingActions.setCurrentProfile(profile));
  dispatch(destroy(TRANSACTION_FORM_NAME));
  dispatch(
    change(TRANSACTION_FORM_NAME, TransactionEnum.StudentIdSeed, profile.IdSeed)
  );
  dispatch(IndividualServingActions.addQuickServeItem());
  if (Array.isArray(profile.error) && profile.error.length > 0) {
    dispatch(DialogActions.addNotificationDialogs(profile.error));
  }
  dispatch(push(`${POS_ROUTES.TEACHER}/${profile.Id}`));
};

export const resetAllStudentsBoughtItems = () => ({
  type: types.RESET_ALL_STUDENTS_BOUGHT_ITEMS,
});

export const resetAllStudentsSelected = () => ({
  type: types.RESET_ALL_STUDENTS_SELECTED,
});

export const resetAllStudentsError = () => ({
  type: types.RESET_ALL_STUDENTS_ERROR,
});

export const resetStudentSelected = idSeed => ({
  type: types.RESET_STUDENT_SELECTED,
  payload: idSeed,
});

export const resetStudentBoughtItems = idSeed => ({
  type: types.RESET_STUDENT_BOUGHT_ITEMS,
  payload: idSeed,
});

export const addItemToStudents = (idSeeds, item) => ({
  type: types.ADD_ITEM_TO_STUDENTS,
  payload: {
    students: idSeeds,
    item,
  },
});

export const setSelectAllStudents = value => ({
  type: types.SET_SELECT_ALL_STUDENTS,
  payload: value,
});

/** Selected student for individual serving, want to get out and unselect the student */
export const removeFromTeacher = idSeed => dispatch => {
  dispatch(IndividualServingActions.clearCurrentProfile());
  dispatch(IndividualServingActions.clearCurrentTransaction());
  dispatch(resetStudentSelected(idSeed));
  dispatch(replace(POS_ROUTES.TEACHER));
};

export const setNumProcessed = numProcessed => ({
  type: types.SET_NUM_PROCESSED,
  payload: numProcessed,
});

export const setNumProcessing = processing => ({
  type: types.SET_NUM_PROCESSING,
  payload: processing,
});



// WEBPACK FOOTER //
// ./src/pages/pointofsale/modules/teacher/state/actions.js